/* eslint-disable react-hooks/exhaustive-deps */
import { useRouter } from 'next/router'
import { Row, Button, Form } from 'antd'
import CreditCardForm from './CreditCardForm'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { getCurrencyToken, getFormatedPrice } from 'src/core/helpers'
import { useAppDispatch, useAppSelector, useWindowSize } from 'src/app/hooks'
import { CustomInputTotalAmount, Loader, CustomText, CustomCol } from 'src/components'
import {
  selectCurrencyToken,
  createBankTransferDeposit,
  createPayPalDeposit,
  createCreditCardDeposit,
  resetDepositSuccessState,
  resetDepositPayPalUrlState,
  currencyListSelector,
  summaryDepositSelector
} from 'src/features'

const DepositForm: React.FC<{ handleCancel: () => void }> = ({ handleCancel }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [form] = Form.useForm()
  const { isMobile } = useWindowSize()
  const currency_code = useAppSelector(selectCurrencyToken)
  const currencyList = useAppSelector(currencyListSelector)
  const {
    response,
    loading,
    success,
    payPalUrl,
    creditCardInfo,
  } = useAppSelector(summaryDepositSelector)

  const [totalAmount, setTotalAmount] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState('')

  const onFinish = () => {
    resetForm()
    router.push(`/deposits?id=${response?.id}`)
  }

  const resetForm = () => {
    form.resetFields()
    setTotalAmount(null)
  }

  const paymentCallback = ({ token }: { token: string }) => {
    dispatch(createCreditCardDeposit({ amount: Number(totalAmount), token }))
    resetForm()
  }

  const handleOnChangeInputAmount = (value: { value: number }) => {
    setTotalAmount(Number(value))
  }

  const onSubmit = (values: { totalAmount: number }) => {
    const payload = {
      amount: Number(values.totalAmount),
      currency_token: getCurrencyToken(currencyList, currency_code),
      kind: paymentMethod
    }
    if (paymentMethod === 'bank') {
      dispatch(createBankTransferDeposit(payload))
    } else if (paymentMethod === 'paypal') {
      dispatch(createPayPalDeposit(payload))
    }
    setPaymentMethod('')
  }

  useEffect(() => {
    if (success) {
      onFinish()
      handleCancel()
    }
    return (() => {
      dispatch(resetDepositSuccessState())
      dispatch(resetDepositPayPalUrlState())
    })
  }, [success])

  useEffect(() => {
    if (payPalUrl !== '') {
      resetForm()
      location.assign(payPalUrl)
    }
  }, [payPalUrl])

  useEffect(() => {
    window['CollectJS']?.configure({
      variant: 'lightbox', callback: paymentCallback
    })
  }, [totalAmount, paymentCallback])

  function depositCreditCard() {
    form.validateFields(['totalAmount'])
    if (totalAmount > 4) {
      window['CollectJS']?.startPaymentRequest()
    }
  }

  return <>
    {loading ? <Loader /> : <>
      <Form
        name='basic'
        autoComplete='off'
        form={form}
        onFinish={onSubmit}
      >
        {/* Total Amount */}
        <CustomText label={t('Summary.Amount')} />
        <CustomInputTotalAmount
          required
          name='totalAmount'
          placeholder={t('Summary.Amount')}
          onChange={handleOnChangeInputAmount}
        />

        <div className='flex-space-between mb-5'>
          <CustomText label={t('Summary.Total')} />
          <CustomText label={getFormatedPrice(totalAmount, currency_code)} />
        </div>

        <Row>
          <CustomCol className={!isMobile && 'pr-1'} xs={12}>
            <Button
              size='large'
              type='primary'
              htmlType='submit'
              className='radius-100'
              onClick={() => setPaymentMethod('bank')}
              block
            >
              {t('Summary.BankTransfer')}
            </Button>
          </CustomCol>
          <CustomCol className={!isMobile && 'pl-1'} xs={12}>
            <Button
              size='large'
              type='primary'
              htmlType='submit'
              className='radius-100'
              block
              onClick={() => setPaymentMethod('paypal')}
            >
              {t('Summary.PayWithPayPal')}
            </Button>
          </CustomCol>
        </Row>

        <Button
          block
          type='primary'
          size='large'
          className='radius-100 mt-3'
          onClick={depositCreditCard}
        >
          {t('Summary.PayWithCreditCard')}
        </Button>
      </Form>

      <CreditCardForm payload={creditCardInfo} />
    </>}
  </>
}

export default DepositForm
